import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { X } from "lucide-react";
import { useHeader } from "../../../Providers/HeaderContextProvider";
import amplitudeService from "../../../Services/amplitude";
import useCorporateBranding from "../hooks/useCorporateBranding";
import CorporateHeader from "../components/CorporateHeader";
import StepperPills from "../components/StepperPills";
import UnavailablePage from "../components/UnavailablePage";
import HeroImage from "./sections/HeroImage";
import Hero from "./sections/Hero";
import WhatThisIs from "./sections/WhatThisIs";
import WhenToUse from "./sections/WhenToUse";
import WhereItFits from "./sections/WhereItFits";
import PrivateByDesign from "./sections/PrivateByDesign";
import ClosingTagline from "./sections/ClosingTagline";
import Footer from "./sections/Footer";
import ActivationForm from "./ActivationForm";
import "./style.css";

/**
 * Default sections used when the API hasn't published any content yet — keeps
 * the page from rendering as a blank shell during corporate setup. These are
 * generic Aris copy; once admins publish branded content, the API response
 * overrides everything.
 */
const DEFAULT_SECTIONS = {
  hero: {
    headline: "Think *clearly* in the moments that matter",
    subtext:
      "A private space to work through pressure, stress, and real-life situations as they happen.",
  },
  what_this_is: {
    title: "What this is",
    description:
      "Aris helps you understand what's actually going on – and what to do next. Just talk it through (voice or text). It helps you slow down, think clearly, and move forward.",
    pill_chips: ["Voice", "Text"],
  },
  cta: {
    card_heading: "Activate Your Access",
    card_subtitle:
      "This confirms your eligibility. Takes less than a minute.",
    button_label: "Activate Access",
  },
  step_labels: { labels: ["Verify", "Set up", "Start using"] },
  footer: { sponsor_line: "Powered by Aris", copyright_line: "© 2026 Aris" },
};

const SkeletonChunk = ({ className = "" }) => (
  <div className={`animate-pulse rounded-md bg-[#e4e4e7] ${className}`} />
);

const ActivationLoadingSkeleton = () => (
  <div className="max-w-[1440px] mx-auto px-4 md:px-9 pt-12 grid md:grid-cols-[1fr_584px] gap-10">
    <div className="flex flex-col gap-6">
      <SkeletonChunk className="h-16 w-3/4" />
      <SkeletonChunk className="h-16 w-2/3" />
      <SkeletonChunk className="h-6 w-full" />
      <SkeletonChunk className="h-48 w-full" />
      <SkeletonChunk className="h-72 w-full" />
    </div>
    <div className="flex flex-col gap-6">
      <SkeletonChunk className="h-32 w-full" />
      <SkeletonChunk className="h-96 w-full" />
    </div>
  </div>
);

const ActivationPage = () => {
  const { slug } = useParams();
  const { setHeaderContent } = useHeader();
  const { branding, content, departments, roles, loading, notFound } =
    useCorporateBranding(slug, "activation");
  const [mobileFormOpen, setMobileFormOpen] = useState(false);

  // Hide the global Aris header on the corporate activation page — we render
  // our own co-branded header instead.
  useEffect(() => {
    setHeaderContent?.(null);
  }, [setHeaderContent]);

  // Lock body scroll while the mobile form sheet is open so the page underneath
  // doesn't scroll along with the modal content.
  useEffect(() => {
    if (!mobileFormOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileFormOpen]);

  // Fire telemetry once we know the activation page actually rendered
  // (i.e. we have branding — not the unavailable fallback).
  useEffect(() => {
    if (!loading && !notFound && slug) {
      try {
        amplitudeService.track?.("corporate_activation_landed", {
          slug,
          corporate_name: branding?.name,
        });
      } catch (_) {
        // amplitude may not be initialized for unauthenticated users — ignore.
      }
    }
  }, [loading, notFound, slug, branding?.name]);

  const sections = useMemo(() => {
    const apiSections = content?.sections || {};
    return {
      hero_image: apiSections.hero_image || null,
      hero: apiSections.hero || DEFAULT_SECTIONS.hero,
      what_this_is: apiSections.what_this_is || DEFAULT_SECTIONS.what_this_is,
      when_to_use: apiSections.when_to_use,
      where_it_fits: apiSections.where_it_fits,
      private_by_design: apiSections.private_by_design,
      closing_tagline: apiSections.closing_tagline,
      footer: apiSections.footer || DEFAULT_SECTIONS.footer,
      cta: apiSections.cta || DEFAULT_SECTIONS.cta,
      step_labels: apiSections.step_labels || DEFAULT_SECTIONS.step_labels,
    };
  }, [content]);

  // Component lookup for the reorderable middle column. Order is driven by
  // the API's `section_order` (per-corporate config); falls back to the
  // default registry order.
  const MIDDLE_COMPONENTS = {
    hero: Hero,
    what_this_is: WhatThisIs,
    when_to_use: WhenToUse,
    where_it_fits: WhereItFits,
    private_by_design: PrivateByDesign,
    closing_tagline: ClosingTagline,
  };

  const DEFAULT_MIDDLE_ORDER = [
    "hero",
    "what_this_is",
    "when_to_use",
    "where_it_fits",
    "private_by_design",
    "closing_tagline",
  ];

  // Sections that show a leading "01"/"02"/etc. badge. The number is assigned
  // by render-order position, not section identity.
  const NUMBERED_SECTION_KEYS = new Set([
    "what_this_is",
    "when_to_use",
    "where_it_fits",
    "private_by_design",
  ]);

  const middleOrder = (() => {
    const apiOrder = Array.isArray(content?.section_order)
      ? content.section_order
      : null;
    if (!apiOrder || apiOrder.length === 0) return DEFAULT_MIDDLE_ORDER;
    const reorderable = apiOrder.filter((k) => MIDDLE_COMPONENTS[k]);
    DEFAULT_MIDDLE_ORDER.forEach((k) => {
      if (!reorderable.includes(k)) reorderable.push(k);
    });
    return reorderable;
  })();

  if (notFound) return <UnavailablePage />;
  if (loading) return <ActivationLoadingSkeleton />;

  const themeStyle = {
    "--corporate-primary": branding?.primary_color || "#4377fc",
    "--corporate-secondary": branding?.secondary_color || "#0d5eaf",
    "--corporate-font-scale":
      typeof branding?.content_font_scale === "number"
        ? branding.content_font_scale
        : 1,
  };

  const displayName = branding?.display_name || branding?.name || "";
  const heroHeadline = sections.hero?.headline || "";
  const buttonLabel = sections.cta?.button_label || "Activate Access";

  const ctaCard = (
    <div className="corp-cta-card-shadow">
      <div className="corp-cta-card-header">
        <div className="flex flex-col gap-3">
          <h2 className="text-2xl font-semibold text-white m-0">
            {sections.cta.card_heading}
          </h2>
          {sections.cta.card_subtitle && (
            <p className="text-sm text-white/90 leading-relaxed m-0">
              {sections.cta.card_subtitle}
            </p>
          )}
        </div>
        <StepperPills
          labels={sections.step_labels.labels}
          activeIndex={0}
        />
      </div>
      <div className="corp-cta-card-body">
        <ActivationForm
          slug={slug}
          orgName={displayName}
          departments={departments}
          roles={roles}
          buttonLabel={buttonLabel}
        />
      </div>
    </div>
  );

  return (
    <div className="corp-activation" style={themeStyle}>
      <Helmet>
        <title>{displayName ? `${displayName} — Aris` : "Aris"}</title>
        <meta property="og:title" content={`${displayName} — Aris`} />
        {branding?.logo_url && (
          <meta property="og:image" content={branding.logo_url} />
        )}
        <meta property="og:description" content={heroHeadline} />
        <link
          href="https://fonts.googleapis.com/css2?family=Urbanist:ital,wght@0,400;0,500;0,600;0,700;1,500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <CorporateHeader branding={branding} />

      <div className="max-w-[1440px] mx-auto px-4 md:px-9 pb-32 lg:pb-16 pt-8 md:pt-12">
        {sections.hero_image?.image_url && (
          <div className="mb-10">
            <HeroImage section={sections.hero_image} />
          </div>
        )}
        <div className="flex flex-col-reverse lg:flex-row lg:items-start gap-10">
          {/* Left column — sections (order driven by per-corporate config).
              Numbered sections renumber 01, 02, 03... based on the rendered
              order rather than the section identity, so reordering keeps
              the numbers contiguous. */}
          <div className="flex flex-col gap-11 flex-1 max-w-full lg:max-w-[744px]">
            {(() => {
              let numberedCounter = 0;
              return middleOrder.map((key) => {
                const Component = MIDDLE_COMPONENTS[key];
                if (!Component) return null;
                const isNumbered = NUMBERED_SECTION_KEYS.has(key);
                const numberProp = isNumbered
                  ? String(++numberedCounter).padStart(2, "0")
                  : undefined;
                return (
                  <Component
                    key={key}
                    section={sections[key]}
                    number={numberProp}
                  />
                );
              });
            })()}
          </div>

          {/* Right column — CTA + form (desktop only). On mobile the form
              opens in a bottom-sheet via the sticky CTA bar below. */}
          <div className="hidden lg:block lg:w-[584px] lg:flex-shrink-0">
            {ctaCard}
          </div>
        </div>

        <Footer section={sections.footer} />
      </div>

      {/* Mobile sticky CTA — opens the form in a bottom sheet */}
      <div className="lg:hidden corp-mobile-cta-bar">
        <button
          type="button"
          onClick={() => setMobileFormOpen(true)}
          className="corp-primary-button"
        >
          {buttonLabel}
        </button>
      </div>

      {/* Mobile bottom-sheet form */}
      {mobileFormOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[60]"
          role="dialog"
          aria-modal="true"
          aria-label={sections.cta.card_heading || "Activate access"}
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileFormOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[95dvh] overflow-y-auto bg-white rounded-t-[20px] shadow-2xl">
            <div className="sticky top-0 z-10 flex justify-end px-3 pt-3 pb-1 bg-white">
              <button
                type="button"
                onClick={() => setMobileFormOpen(false)}
                className="size-9 rounded-full border border-[#d0d5dd] bg-white inline-flex items-center justify-center cursor-pointer text-black"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-4 pb-6">{ctaCard}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivationPage;
